import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useApi } from '@/hooks/useApi';
import { Camera, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Html5QrcodeScanner } from 'html5-qrcode';

export default function QRScannerPage() {
  const { token } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  
  const [scanning, setScanning] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(false);
  const scannerRef = useRef(null);
  const html5QrcodeScannerRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup scanner on unmount
      if (html5QrcodeScannerRef.current) {
        html5QrcodeScannerRef.current.clear().catch(err => console.error('Scanner cleanup error:', err));
      }
    };
  }, []);

  const startScanner = () => {
    setScanning(true);
    
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      try {
        // Initialize scanner
        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          showTorchButtonIfSupported: true
        };

        const scanner = new Html5QrcodeScanner('qr-reader', config, false);
        html5QrcodeScannerRef.current = scanner;

        scanner.render(onScanSuccess, onScanFailure);
      } catch (error) {
        console.error('Scanner initialization error:', error);
        toast.error('Failed to start camera. Please check permissions.');
        setScanning(false);
      }
    }, 100);
  };

  const onScanSuccess = async (decodedText, decodedResult) => {
    console.log('QR Code scanned:', decodedText);
    
    // Stop scanner
    if (html5QrcodeScannerRef.current) {
      html5QrcodeScannerRef.current.clear();
    }
    setScanning(false);

    // Parse QR data
    try {
      const qrData = JSON.parse(decodedText);
      const appointmentId = qrData.id;
      
      // Fetch appointment details
      setLoading(true);
      const aptData = await api.get(`/appointments/by-qr/${appointmentId}`);
      setAppointment(aptData);
      toast.success('Appointment found!');
    } catch (error) {
      console.error('Failed to fetch appointment:', error);
      toast.error('Invalid QR code or appointment not found');
    } finally {
      setLoading(false);
    }
  };

  const onScanFailure = (error) => {
    // Ignore continuous scan errors
    if (error && !error.toString().includes('NotFoundException')) {
      console.warn('QR scan error:', error);
    }
  };

  const stopScanner = () => {
    if (html5QrcodeScannerRef.current) {
      html5QrcodeScannerRef.current.clear();
    }
    setScanning(false);
  };

  const markAsCompleted = async () => {
    try {
      await api.patch(`/appointments/${appointment.id}/status`, {
        status: 'completed'
      });
      toast.success('Appointment marked as completed');
      setAppointment({ ...appointment, status: 'completed' });
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update appointment status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">QR Code Scanner</h1>
            <p className="text-sm text-gray-500">Scan appointment QR codes for check-in</p>
          </div>
        </div>

        {/* Scanner Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-purple-600" />
              Scan QR Code
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!scanning && !appointment && (
              <div className="text-center py-12">
                <Camera className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-600 mb-4">Click below to start scanning</p>
                <Button onClick={startScanner} className="bg-purple-600 hover:bg-purple-700">
                  <Camera className="h-4 w-4 mr-2" />
                  Start Scanner
                </Button>
              </div>
            )}

            {scanning && (
              <div className="space-y-4">
                <div id="qr-reader" className="w-full"></div>
                <div className="text-center">
                  <Button variant="outline" onClick={stopScanner}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading appointment...</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appointment Details */}
        {appointment && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Appointment Details
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => {
                  setAppointment(null);
                  startScanner();
                }}>
                  Scan Another
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Patient</label>
                  <p className="text-gray-900 font-semibold text-lg">{appointment.patient_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Doctor</label>
                  <p className="text-gray-900">{appointment.doctor_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Date</label>
                  <p className="text-gray-900">{formatDate(appointment.appointment_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Time</label>
                  <p className="text-gray-900">{appointment.appointment_time}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Type</label>
                  <p className="text-gray-900 capitalize">{appointment.appointment_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    {appointment.status === 'completed' ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        Completed
                      </span>
                    ) : appointment.status === 'scheduled' ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        Scheduled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                        {appointment.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {appointment.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Notes</label>
                  <p className="text-gray-900 mt-1">{appointment.notes}</p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                {appointment.status !== 'completed' && (
                  <Button onClick={markAsCompleted} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark as Completed
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => navigate(`/patient/${appointment.patient_id}`)}
                >
                  View Patient Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
